import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import {
  CaretLeft, Star, CheckCircle, XCircle, Clock,
  Plus, Trash, MagnifyingGlass, ChatText, Camera,
} from "@phosphor-icons/react";
import apiClient from "../../lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  approved: { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", label: "Aprobada", Icon: CheckCircle },
  pending:  { cls: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20", label: "Pendiente", Icon: Clock },
  rejected: { cls: "bg-red-500/10 text-red-400 border-red-500/20", label: "Rechazada", Icon: XCircle },
};

function StarRow({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star key={i} weight={i <= rating ? "fill" : "regular"} className={`w-3.5 h-3.5 ${i <= rating ? "text-yellow-400" : "text-zinc-700"}`} />
      ))}
    </div>
  );
}

export default function AdminReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [form, setForm] = useState({ user_name: "", user_plan: "vip", rating: 5, text: "", auto_approve: true, _avatarFile: null });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/admin/reviews");
      setReviews(r.data.reviews || []);
    } catch (err) { console.error("[AdminReviews] failed:", err?.response?.data || err.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    setActionLoading(id + status);
    try {
      await apiClient.put(`/admin/reviews/${id}/status`, { status });
      await load();
      toast.success(status === "approved" ? "Reseña aprobada" : status === "rejected" ? "Reseña rechazada" : "Estado actualizado");
    } catch { toast.error("Error al actualizar"); }
    setActionLoading(null);
  };

  const deleteReview = async (id) => {
    if (!window.confirm("¿Eliminar esta reseña?")) return;
    setActionLoading(id + "del");
    try {
      await apiClient.delete(`/admin/reviews/${id}`);
      await load();
      toast.success("Reseña eliminada");
    } catch { toast.error("Error al eliminar"); }
    setActionLoading(null);
  };

  const createReview = async (e) => {
    e.preventDefault();
    if (!form.user_name.trim() || !form.text.trim()) return toast.error("Completa todos los campos");
    try {
      const res = await apiClient.post("/admin/reviews", form);
      const newId = res.data.id;
      // Upload avatar if selected
      if (form._avatarFile && newId) {
        const fd = new FormData();
        fd.append("file", form._avatarFile);
        await apiClient.post(`/admin/reviews/${newId}/avatar`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      await load();
      setShowCreateForm(false);
      setForm({ user_name: "", user_plan: "vip", rating: 5, text: "", auto_approve: true, _avatarFile: null });
      toast.success("Reseña creada");
    } catch { toast.error("Error al crear reseña"); }
  };

  const filtered = reviews.filter(r =>
    r.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    r.text?.toLowerCase().includes(search.toLowerCase())
  );

  const summary = {
    total: reviews.length,
    approved: reviews.filter(r => r.status === "approved").length,
    pending: reviews.filter(r => r.status === "pending").length,
    rejected: reviews.filter(r => r.status === "rejected").length,
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-white/[0.06] bg-surface/50 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4 flex items-center gap-4">
          <Link to="/admin" className="text-zinc-500 hover:text-foreground transition-colors">
            <CaretLeft weight="bold" className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <ChatText weight="duotone" className="w-5 h-5 text-yellow-400" />
            <span className="font-bold text-lg">Gestión de Reseñas</span>
          </div>
          <button
            data-testid="admin-reviews-create-btn"
            onClick={() => setShowCreateForm(v => !v)}
            className="ml-auto btn-primary text-sm"
          >
            <Plus weight="bold" className="w-4 h-4" /> Nueva reseña
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total", value: summary.total, color: "text-foreground" },
            { label: "Aprobadas", value: summary.approved, color: "text-emerald-400" },
            { label: "Pendientes", value: summary.pending, color: "text-yellow-400" },
            { label: "Rechazadas", value: summary.rejected, color: "text-red-400" },
          ].map(s => (
            <div key={s.label} className="surface rounded-xl p-4 text-center">
              <div className={`text-3xl font-black number-ticker ${s.color}`}>{s.value}</div>
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Create form */}
        {showCreateForm && (
          <div className="surface rounded-2xl p-6">
            <div className="text-sm font-bold mb-4">Crear reseña manualmente</div>
            <form onSubmit={createReview} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold block mb-1.5">Nombre del usuario</label>
                <input className="input-field w-full" placeholder="Ej: Carlos M." value={form.user_name}
                  onChange={e => setForm(f => ({ ...f, user_name: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold block mb-1.5">Plan</label>
                <select className="input-field w-full" value={form.user_plan}
                  onChange={e => setForm(f => ({ ...f, user_plan: e.target.value }))}>
                  <option value="vip">VIP</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold block mb-1.5">Estrellas</label>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(n => (
                    <button type="button" key={n} onClick={() => setForm(f => ({ ...f, rating: n }))}
                      className="p-1">
                      <Star weight={n <= form.rating ? "fill" : "regular"}
                        className={`w-5 h-5 ${n <= form.rating ? "text-yellow-400" : "text-zinc-600"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold block mb-1.5">Aprobar automáticamente</label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.auto_approve} onChange={e => setForm(f => ({ ...f, auto_approve: e.target.checked }))}
                    className="w-4 h-4 rounded" />
                  <span className="text-sm text-zinc-300">Publicar de inmediato en la landing</span>
                </label>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold block mb-1.5">
                  Foto del usuario <span className="text-zinc-600 font-normal normal-case">(opcional, JPG/PNG/WebP, máx 5MB)</span>
                </label>
                <div className="flex items-center gap-4">
                  {avatarPreview
                    ? <img src={avatarPreview} alt="preview" className="w-14 h-14 rounded-full object-cover border-2 border-yellow-400/30" />
                    : <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-white/[0.1] flex items-center justify-center">
                        <Camera className="w-5 h-5 text-zinc-500" />
                      </div>
                  }
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                    className="btn-secondary text-xs px-3 py-1.5">
                    {avatarPreview ? "Cambiar foto" : "Subir foto"}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                    data-testid="admin-review-avatar-input"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setForm(prev => ({ ...prev, _avatarFile: f }));
                      setAvatarPreview(URL.createObjectURL(f));
                    }} />
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-zinc-500 uppercase tracking-wider font-bold block mb-1.5">Texto de la reseña</label>
                <textarea className="input-field w-full h-24 resize-none" placeholder="Escribe la reseña..."
                  value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))} />
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="btn-primary text-sm" data-testid="admin-reviews-save-btn">Guardar reseña</button>
                <button type="button" className="btn-secondary text-sm" onClick={() => { setShowCreateForm(false); setAvatarPreview(null); }}>Cancelar</button>
              </div>
            </form>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <MagnifyingGlass className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="admin-reviews-search" className="input-field pl-10 w-full max-w-sm"
            placeholder="Buscar por usuario o texto..." value={search}
            onChange={e => setSearch(e.target.value)} />
        </div>

        {/* List */}
        <div className="space-y-3">
          {loading ? (
            <div className="surface rounded-xl p-12 text-center text-zinc-500">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="surface rounded-xl p-12 text-center text-zinc-500">No se encontraron reseñas.</div>
          ) : filtered.map(r => {
            const st = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
            return (
              <div key={r.id} data-testid={`admin-review-row-${r.id}`}
                className="surface rounded-xl p-5 flex flex-col md:flex-row md:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center text-xs font-bold text-yellow-400">
                      {(r.user_name || "?")[0].toUpperCase()}
                    </div>
                    <div>
                      <span className="font-semibold text-sm">{r.user_name}</span>
                      <span className="text-xs text-zinc-500 ml-2 uppercase tracking-wider">{r.user_plan}</span>
                      {r.is_seeded && <span className="text-[10px] text-zinc-600 ml-2">(ejemplo)</span>}
                    </div>
                    <StarRow rating={r.rating} />
                    <span className={`chip text-[11px] ${st.cls}`}>
                      <st.Icon className="w-3 h-3" weight="fill" /> {st.label}
                    </span>
                  </div>
                  <p className="text-sm text-zinc-300 leading-relaxed">{r.text}</p>
                  <div className="text-[10px] text-zinc-600 mt-2 font-mono">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString("es-ES") : "—"}
                  </div>
                </div>
                <div className="flex md:flex-col gap-2 flex-shrink-0">
                  {r.status !== "approved" && (
                    <button data-testid={`admin-review-approve-${r.id}`}
                      onClick={() => updateStatus(r.id, "approved")}
                      disabled={actionLoading === r.id + "approved"}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-all disabled:opacity-50">
                      <CheckCircle className="w-3.5 h-3.5" /> Aprobar
                    </button>
                  )}
                  {r.status !== "rejected" && (
                    <button data-testid={`admin-review-reject-${r.id}`}
                      onClick={() => updateStatus(r.id, "rejected")}
                      disabled={actionLoading === r.id + "rejected"}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all disabled:opacity-50">
                      <XCircle className="w-3.5 h-3.5" /> Rechazar
                    </button>
                  )}
                  <button data-testid={`admin-review-delete-${r.id}`}
                    onClick={() => deleteReview(r.id)}
                    disabled={actionLoading === r.id + "del"}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-white/[0.08] text-zinc-500 hover:text-red-400 hover:border-red-500/20 transition-all disabled:opacity-50">
                    <Trash className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
